import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Check, CreditCard, Loader2, AlertCircle, 
  ChevronRight, ExternalLink, Zap
} from 'lucide-react';
import { MobileLayout } from './ui';
import useSubscription from '@/hooks/useSubscription';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const SubscriptionPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const { 
    subscription, 
    plans, 
    isLoading, 
    createCheckoutSession,
    createBillingPortalSession 
  } = useSubscription();

  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const handleSubscribe = async (planId: string) => {
    setIsProcessing(true);
    setSelectedPlan(planId);

    try {
      const { checkout_url } = await createCheckoutSession(planId);
      if (checkout_url) {
        window.location.href = checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handleManageBilling = async () => {
    setIsProcessing(true);

    try {
      const { portal_url } = await createBillingPortalSession();
      if (portal_url) {
        window.location.href = portal_url;
      }
    } catch (error) {
      console.error('Portal error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <MobileLayout title="Subscription" showBack backTo="/brand/campaign">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 300 
        }}>
          <div className="mobile-spinner" />
        </div>
      </MobileLayout>
    );
  }

  const hasActiveSubscription = subscription?.has_subscription && subscription?.status === 'active';
  const isTrialing = subscription?.has_subscription && subscription?.status === 'trialing';
  const isTrialEnded = subscription?.has_subscription && subscription?.status === 'paused';

  return (
    <MobileLayout title="Subscription" showBack backTo="/brand/campaign">
      <SEO
        title="Subscription Plans"
        description="Choose the right BrandComply plan for your team. Flexible pricing for influencer and UGC video compliance at any scale."
        path="/subscription"
      />
      <div style={{ padding: 16 }}>
        {/* Current Plan Status */}
        {hasActiveSubscription && (
          <div style={{
            background: 'linear-gradient(135deg, #243837 0%, #3a5654 100%)',
            borderRadius: 16,
            padding: 20,
            marginBottom: 24,
            color: '#fff',
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8, 
              marginBottom: 12,
              color: PALETTE.inchworm,
            }}>
              <Zap size={20} />
              <span style={{ fontWeight: 600 }}>Active Plan</span>
            </div>

            <h2 style={{ margin: '0 0 8px', fontSize: 24 }}>
              {subscription?.plan_name}
            </h2>
            
            <p style={{ margin: 0, opacity: 0.8, fontSize: 14 }}>
              ${subscription?.monthly_price}/month
            </p>

            {/* Usage */}
            <div style={{ marginTop: 20 }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: 8,
                fontSize: 14,
              }}>
                <span>Video Checks Used</span>
                <span>{subscription?.current_usage} / {subscription?.monthly_limit}</span>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 4,
                height: 8,
                overflow: 'hidden',
              }}>
                <div style={{
                  background: PALETTE.inchworm,
                  height: '100%',
                  width: `${Math.min(100, (subscription?.current_usage || 0) / (subscription?.monthly_limit || 1) * 100)}%`,
                  borderRadius: 4,
                  transition: 'width 0.3s',
                }} />
              </div>
              <p style={{ 
                margin: '8px 0 0', 
                fontSize: 13, 
                opacity: 0.7 
              }}>
                {subscription?.remaining} checks remaining
              </p>
            </div>

            {/* Period End */}
            {subscription?.current_period_end && (
              <p style={{ 
                margin: '16px 0 0', 
                fontSize: 13, 
                opacity: 0.7 
              }}>
                Renews {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {/* Usage Warning */}
        {subscription && !subscription.can_analyze && (
          <div style={{
            background: 'rgba(254, 119, 51, 0.1)',
            border: '1px solid rgba(254, 119, 51, 0.3)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <AlertCircle size={20} color={PALETTE.orange} />
              <span style={{ fontWeight: 600, color: PALETTE.orange }}>Limit Reached</span>
            </div>
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
              The video check limit has been reached.   
              Please contact your agency/brand. 
            </p>
          </div>
        )}

        {isTrialing && (
          <div style={{ 
            background: `${PALETTE.inchworm}10`, 
            border: `1px solid ${PALETTE.inchworm}30`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            textAlign: 'center'
          }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: PALETTE.gunmetal, margin: 0 }}>
              14-day free trial — {(subscription as any)?.trial_days_remaining ?? 0} days remaining
            </p>
            <p style={{ fontSize: 13, color: `${PALETTE.gunmetal}99`, margin: '4px 0 0' }}>
              30 video checks included · No credit card required
            </p>
          </div>
        )}

        {/* Plans */}
        <h3 style={{ 
          fontSize: 18, 
          marginBottom: 16, 
          color: PALETTE.gunmetal 
        }}>
          {hasActiveSubscription ? 'Change Plan' : isTrialEnded ? 'Your trial has ended — choose a plan' : isTrialing ? 'Subscribe anytime to continue after your trial' : 'Choose a Plan'}
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {plans.map((plan) => {
            const isCurrentPlan = subscription?.plan_id === plan.id;
            const isPro = plan.id === 'pro';
            const subtitle = isPro
              ? 'For growing agencies & mid-sized marketing teams'
              : 'For freelancers & small marketing teams';

            const features = [
              <><strong>{plan.monthly_video_limit}</strong> video checks per month</>,
              'Unlimited campaigns',
              'Unlimited shareable submission links',
              ...(isPro ? ['Guided onboarding'] : []),
            ];

            return (
              <div
                key={plan.id}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: isPro ? `2px solid ${PALETTE.inchworm}` : '1px solid #e0e0e0',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {isPro && (
                  <div style={{
                    background: PALETTE.inchworm,
                    color: PALETTE.gunmetal,
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '4px 12px',
                    textAlign: 'center',
                  }}>
                    BEST VALUE
                  </div>
                )}

                <div style={{ padding: 20 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: 4,
                  }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 20, color: PALETTE.gunmetal }}>
                        {plan.name}
                      </h4>
                      <p style={{ margin: '4px 0 0', color: '#666' }}>
                        <span style={{ fontSize: 28, fontWeight: 700, color: PALETTE.gunmetal }}>
                          €{plan.monthly_price}
                        </span>
                        <span style={{ fontSize: 14 }}>/month</span>
                      </p>
                    </div>
                    {isCurrentPlan && (
                      <span style={{
                        background: PALETTE.inchworm,
                        color: PALETTE.gunmetal,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 12,
                      }}>
                        Current
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '0 0 16px', fontSize: 13, color: '#999' }}>
                    {subtitle}
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: 16 }}>
                    {features.map((feature, fi) => (
                      <div key={fi} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        marginBottom: fi < features.length - 1 ? 8 : 0,
                      }}>
                        <Check size={18} color="#16a34a" />
                        <span style={{ fontSize: 14 }}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {(!isCurrentPlan || isTrialing || isTrialEnded) && (
                    <button
                      className={`mobile-btn mobile-btn-full ${isPro ? 'mobile-btn-primary' : 'mobile-btn-outline'}`}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={isProcessing}
                    >
                      {isProcessing && selectedPlan === plan.id ? (
                        <>
                          <Loader2 size={18} className="mobile-spinner" style={{ width: 18, height: 18, marginRight: 8 }} />
                          Processing...
                        </>
                      ) : hasActiveSubscription && !isTrialing && !isTrialEnded ? (
                        'Switch to this plan'
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Custom Plan Card */}
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              border: '1px solid #e0e0e0',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontSize: 20, color: PALETTE.gunmetal }}>
                  Custom
                </h4>
                <p style={{ margin: '4px 0 0', color: '#666' }}>
                  <span style={{ fontSize: 28, fontWeight: 700, color: PALETTE.gunmetal }}>
                    Contact us
                  </span>
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#999' }}>
                  For large agencies &amp; enterprise brands
                </p>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Check size={18} color="#16a34a" />
                  <span style={{ fontSize: 14 }}>Custom built packages</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Check size={18} color="#16a34a" />
                  <span style={{ fontSize: 14 }}>Custom branding</span>
                </div>
              </div>

              <button
                className="mobile-btn mobile-btn-outline mobile-btn-full"
                onClick={() => window.location.href = 'mailto:info@brandcomply.ai'}
              >
                Talk to us
                <ExternalLink size={16} style={{ marginLeft: 6 }} />
              </button>
            </div>
          </div>
        </div>

        {/* Manage Billing */}
        {hasActiveSubscription && (
          <button
            onClick={handleManageBilling}
            disabled={isProcessing}
            style={{
              width: '100%',
              marginTop: 24,
              padding: 16,
              background: '#f5f5f5',
              border: 'none',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CreditCard size={20} color="#666" />
              <span style={{ fontWeight: 500, color: PALETTE.gunmetal }}>
                Manage Billing
              </span>
            </div>
            <ExternalLink size={18} color="#999" />
          </button>
        )}

        {/* FAQ */}
        <div style={{ marginTop: 32, paddingBottom: 32 }}>
          <h3 style={{ fontSize: 16, marginBottom: 16, color: PALETTE.gunmetal }}>
            Frequently Asked Questions
          </h3>

          <div className="mobile-card" style={{ marginBottom: 12 }}>
            <div className="mobile-card-content">
              <h4 style={{ fontSize: 14, margin: '0 0 8px', color: PALETTE.gunmetal }}>
                Can I cancel anytime?
              </h4>
              <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
                Yes, you can cancel your subscription at any time from the billing portal.
                You'll continue to have access until the end of your billing period.
              </p>
            </div>
          </div>

          <div className="mobile-card">
            <div className="mobile-card-content">
              <h4 style={{ fontSize: 14, margin: '0 0 8px', color: PALETTE.gunmetal }}>
                What happens when I reach my limit?
              </h4>
              <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
                You can upgrade your plan at any time to get more video checks. Your limit resets at the start of each billing cycle. If you need more than 100 video checks, please contact us at info@brandcomply.ai.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default SubscriptionPageMobile;
